import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { captureAttribution, captureAutomationMarker } from '@shared/services/attributionService';
import { captureNotificationClickFromUrl } from '@shared/services/notificationClickService';
import { AuthProvider } from '../context/AuthContext';
import { DataProvider } from '../context/DataContext';
import { ExerciseThemeProvider } from '../context/ExerciseThemeContext';
import { SubscriptionProvider } from '../context/SubscriptionContext';
import { NotificationProvider } from '../context/NotificationContext';
import { FullscreenLoadingProvider } from '../context/FullscreenLoadingContext';
import { useRemoteConfig } from '../context/RemoteConfigContext';
import { ErrorBoundary } from '../components/ui/ErrorBoundary';
import { PaywallModal } from '../components/subscription/PaywallModal';
import { AuthGuard } from './AuthGuard';
import { ForceUpdateGate } from '../components/ui/ForceUpdateGate';
import { useVersionCheck } from '../hooks/useVersionCheck';
import { PasswordResetAction } from '../components/ui/PasswordResetAction';
// Pre-login Onboarding component preserved at components/ui/Onboarding.tsx for future use
import { lazyWithRetry } from '../utils/lazyWithRetry';
import { isDemoRouteEnabled } from '../demo/demoAccess';

// Demo mode — lazy-loaded so it doesn't bloat the main bundle
const DemoApp = lazyWithRetry(() => import('../demo/DemoApp'));

// Motion sandbox — dev-only animation playground, lazy-loaded (never ships to prod)
const MotionSandbox = lazyWithRetry(() => import('../sandbox/MotionSandbox'));

// Design Lab — dev-only brand-true component iteration harness (loop L8b), lazy-loaded
const DesignLab = lazyWithRetry(() => import('../design-lab/DesignLab'));

/** Onboarding page at /onboarding — redirects to the new welcome screen.
 *  The pre-login Onboarding component is preserved in components/ui/Onboarding.tsx
 *  for future use (e.g., collecting name/intent pre-login to skip post-login steps). */
const OnboardingPage: React.FC = () => <Navigate to="/" replace />;

/** Main app shell with all providers (auth-aware routes) */
const AppShell: React.FC = () => (
    <ExerciseThemeProvider>
        <AuthProvider>
            <SubscriptionProvider>
                <NotificationProvider>
                    <DataProvider>
                        <FullscreenLoadingProvider>
                            <AuthGuard />
                            <PaywallModal />
                        </FullscreenLoadingProvider>
                    </DataProvider>
                </NotificationProvider>
            </SubscriptionProvider>
        </AuthProvider>
    </ExerciseThemeProvider>
);

const App: React.FC = () => {
    const { isLoading } = useRemoteConfig();
    const { tier, platform, currentVersion, requiredVersion } = useVersionCheck();

    // Capture UTM params and attribution data on every app entry. The
    // automation marker (?automation=1, dev/test builds only) must land first
    // so automation sessions never write attribution data — auth resolves
    // async, well after this effect.
    useEffect(() => {
        captureAutomationMarker(import.meta.env.VITE_APP_ENV);
        captureAttribution();
    }, []);

    // Capture a notification click token (`?nt=`) on entry — this is the only path
    // web/PWA taps have (there is no service worker) and it covers deep links.
    // Stashes to localStorage; NotificationContext flushes it once a uid exists.
    useEffect(() => { captureNotificationClickFromUrl(); }, []);

    if (isLoading) {
        return (
            <div className="h-screen w-full flex flex-col items-center justify-center bg-sand">
                <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
                <p className="mt-4 text-growth font-serif italic animate-pulse">Breathing...</p>
            </div>
        );
    }

    // Hard version floor — renders INSTEAD of the router, so there is no route
    // around it and no AuthGuard to slip past. Only ever 'hard' on native
    // (see useVersionCheck), so the dev/web routes below are unaffected.
    if (tier === 'hard') {
        return (
            <ForceUpdateGate
                platform={platform === 'ios' ? 'ios' : 'android'}
                currentVersion={currentVersion}
                requiredVersion={requiredVersion}
            />
        );
    }

    return (
        <ErrorBoundary level="app">
            <Routes>
                {/* Public route — onboarding flow linked from marketing site */}
                <Route path="/onboarding" element={<OnboardingPage />} />

                {/* Firebase auth action handler (password reset, email verification) */}
                <Route path="/__/auth/action" element={<PasswordResetAction />} />

                {/* Demo mode — mock data, no auth required. Dev + CI-test only
                    (the CI smoke job builds with VITE_APP_ENV=test and its
                    visual specs render /demo/* routes). */}
                {isDemoRouteEnabled(import.meta.env.VITE_APP_ENV) ? (
                    <Route path="/demo/*" element={
                        <React.Suspense fallback={
                            <div className="h-screen w-full flex flex-col items-center justify-center bg-sand">
                                <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
                                <p className="mt-4 text-growth font-serif italic animate-pulse">Breathing...</p>
                            </div>
                        }>
                            <DemoApp />
                        </React.Suspense>
                    } />
                ) : (
                    <Route path="/demo/*" element={<Navigate to="/" replace />} />
                )}

                {/* Motion sandbox — dev only, no auth/providers, gated like /demo */}
                {import.meta.env.VITE_APP_ENV === 'development' ? (
                    <Route path="/_sandbox/motion" element={
                        <React.Suspense fallback={
                            <div className="h-screen w-full flex items-center justify-center bg-cloud">
                                <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
                            </div>
                        }>
                            <MotionSandbox />
                        </React.Suspense>
                    } />
                ) : (
                    <Route path="/_sandbox/motion" element={<Navigate to="/" replace />} />
                )}

                {/* Design Lab (L8b) — dev only, no auth/providers, gated like /demo */}
                {import.meta.env.VITE_APP_ENV === 'development' ? (
                    <Route path="/_design-lab" element={
                        <React.Suspense fallback={
                            <div className="h-screen w-full flex items-center justify-center bg-cloud">
                                <div className="w-16 h-16 border-4 border-mist border-t-foliage rounded-full animate-spin"></div>
                            </div>
                        }>
                            <DesignLab />
                        </React.Suspense>
                    } />
                ) : (
                    <Route path="/_design-lab" element={<Navigate to="/" replace />} />
                )}

                {/* All other routes go through the full provider stack + AuthGuard */}
                <Route path="/*" element={<AppShell />} />
            </Routes>
        </ErrorBoundary>
    );
};

export default App;
